import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { QuoteApproverApiService } from '../../services/quote-approver-api.service';
import { QuoteApprovalApiService } from '../../services/quote-approval-api.service';
import {
  QuoteApproverRequest,
  QuoteApproverResponse,
  QuoteSignatureCaptureType
} from '../../models/quote-approver.model';
import { CrmUserResponse } from '../../../users/models/user.model';

type SignTab = 'drawn' | 'typed' | 'uploaded';

/**
 * Form (drawer-only) de Responsable de aprobación de cotizaciones. Captura de firma
 * registrada reutilizando el patrón del portal público (dibujar / escribir / subir).
 * En edición la firma se actualiza vía PUT /api/quote-approvers/{id}/signature; en
 * creación va dentro del POST.
 */
@Component({
  selector: 'app-quote-approver-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './quote-approver-form.component.html',
})
export class QuoteApproverFormComponent implements OnInit {
  @Input() drawerMode = true;

  @Output() saved = new EventEmitter<QuoteApproverResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  approverId: number | null = null;

  // Usuarios activos del negocio (selector userId)
  users: CrmUserResponse[] = [];
  loadingUsers = false;

  // Firma registrada actual (para mostrar "Firma registrada" en edición)
  currentSignatureData: string | null = null;
  currentSignatureType: string | null = null;
  hasSignature = false;

  // ---- Captura de firma ----
  signTab: SignTab = 'drawn';
  /** true cuando el usuario está capturando/reemplazando la firma. */
  capturingSignature = false;

  // Canvas
  private canvas?: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null = null;
  private drawing = false;
  private lastX = 0;
  private lastY = 0;
  hasDrawing = false;

  // Firma tipográfica (texto)
  typedSignature = '';

  // Firma subida (imagen)
  uploadedSignature: string | null = null;
  uploadError = '';

  @ViewChild('signCanvas') set signCanvasRef(ref: ElementRef<HTMLCanvasElement> | undefined) {
    if (ref && ref.nativeElement !== this.canvas) {
      this.canvas = ref.nativeElement;
      this.initCanvas();
    } else if (!ref) {
      this.canvas = undefined;
      this.ctx = null;
    }
  }

  constructor(
    private fb: FormBuilder,
    private api: QuoteApproverApiService,
    private approvalApi: QuoteApprovalApiService,
    private authService: AuthService,
    private toast: ToastService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // En modo drawer el wrapper invoca reset()/loadApprover() al abrir.
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  private initForm(): void {
    this.form = this.fb.group({
      userId: [null, [Validators.required]],
      cargo: ['', [Validators.required, Validators.maxLength(150)]],
      email: ['', [Validators.email, Validators.maxLength(255)]],
      approvalLimit: [null, [Validators.min(0)]],
      isActive: [true],
    });
  }

  reset(): void {
    this.isEditing = false;
    this.approverId = null;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.currentSignatureData = null;
    this.currentSignatureType = null;
    this.hasSignature = false;
    this.resetSignatureCapture();
    this.capturingSignature = true; // en creación siempre se captura
    this.initForm();
    this.cargarUsuarios();
  }

  loadApprover(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.approverId = id;
    this.capturingSignature = false;
    this.cargarUsuarios();
    this.api.getApproverById(id, this.businessId).subscribe({
      next: (item) => {
        this.form.patchValue({
          userId: item.userId,
          cargo: item.cargo,
          email: item.email,
          approvalLimit: item.approvalLimit,
          isActive: item.isActive,
        });
        this.currentSignatureData = item.signatureData || null;
        this.currentSignatureType = item.signatureType || null;
        this.hasSignature = item.hasSignature;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el responsable';
        this.loading = false;
      },
    });
  }

  private cargarUsuarios(): void {
    this.loadingUsers = true;
    this.approvalApi.getActiveUsers(this.businessId).subscribe({
      next: (users) => {
        this.users = users;
        this.loadingUsers = false;
      },
      error: () => {
        this.users = [];
        this.loadingUsers = false;
      }
    });
  }

  userLabel(user: CrmUserResponse): string {
    const name = [user.fullname, user.lastname].filter(Boolean).join(' ').trim() || user.mail;
    return user.roleName ? `${name} — ${user.roleName}` : name;
  }

  /** true si la firma registrada existente es una imagen (data URL). */
  get isCurrentSignatureImage(): boolean {
    return !!this.currentSignatureData && /^data:image\//i.test(this.currentSignatureData);
  }

  // ---- Captura de firma ----

  iniciarCapturaFirma(): void {
    this.resetSignatureCapture();
    this.capturingSignature = true;
  }

  cancelarCapturaFirma(): void {
    this.resetSignatureCapture();
    this.capturingSignature = false;
  }

  private resetSignatureCapture(): void {
    this.signTab = 'drawn';
    this.hasDrawing = false;
    this.typedSignature = '';
    this.uploadedSignature = null;
    this.uploadError = '';
  }

  setSignTab(tab: SignTab): void {
    if (this.signTab === tab) return;
    this.signTab = tab;
    if (tab !== 'drawn') this.hasDrawing = false;
  }

  /** Devuelve la firma capturada (data/type) o null si no hay una válida. */
  private getCapturedSignature(): { data: string; type: QuoteSignatureCaptureType } | null {
    if (this.signTab === 'drawn') {
      if (!this.hasDrawing || !this.canvas) return null;
      return { data: this.canvas.toDataURL('image/png'), type: 'drawn' };
    }
    if (this.signTab === 'typed') {
      const text = this.typedSignature.trim();
      if (!text) return null;
      return { data: text, type: 'typed' };
    }
    if (!this.uploadedSignature) return null;
    return { data: this.uploadedSignature, type: 'uploaded' };
  }

  get typedPreview(): string {
    return this.typedSignature.trim() || 'Firma';
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const captured = this.getCapturedSignature();

    // En creación la firma es obligatoria; en edición solo si el usuario está reemplazándola.
    if (!this.isEditing && !captured) {
      this.error = 'Captura la firma del responsable (dibujar, escribir o subir).';
      return;
    }
    if (this.capturingSignature && !captured) {
      this.error = 'Completa la firma capturada o cancela la captura.';
      return;
    }

    this.saving = true;
    this.error = '';

    const v = this.form.value;
    const body: QuoteApproverRequest = {
      userId: v.userId,
      cargo: (v.cargo || '').trim(),
      approvalLimit: v.approvalLimit !== null && v.approvalLimit !== '' ? Number(v.approvalLimit) : null,
      isActive: v.isActive ?? true,
    };
    const email = (v.email || '').trim();
    if (email) body.email = email;

    if (this.isEditing && this.approverId) {
      // Si se capturó una firma nueva, va en el body del PUT principal.
      if (captured) {
        body.signatureData = captured.data;
        body.signatureType = captured.type;
      }
      this.api.updateApprover(this.approverId, this.businessId, body).subscribe({
        next: (item) => {
          this.saving = false;
          this.toast.updated('Responsable de aprobación');
          this.saved.emit(item);
        },
        error: (err) => this.handleError(err, 'Error al actualizar el responsable'),
      });
    } else {
      if (captured) {
        body.signatureData = captured.data;
        body.signatureType = captured.type;
      }
      this.api.createApprover(this.businessId, body).subscribe({
        next: (item) => {
          this.saving = false;
          this.toast.created('Responsable de aprobación');
          this.saved.emit(item);
        },
        error: (err) => this.handleError(err, 'Error al crear el responsable'),
      });
    }
  }

  private handleError(err: { status?: number; error?: { message?: string } }, fallback: string): void {
    this.saving = false;
    const msg = err.error?.message || fallback;
    this.error = msg;
    if (err?.status === 409 || err?.status === 422) {
      this.toast.error({ title: 'No se pudo guardar', message: msg });
    } else {
      this.toast.error({ title: 'Error', message: msg });
    }
  }

  cancelar(): void {
    this.cancelled.emit();
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['email']) return 'Correo no válido';
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    if (c.errors['min']) return 'El valor no puede ser negativo';
    return 'Campo inválido';
  }

  // ---- Canvas de firma manuscrita (mismo patrón que el portal) ----

  private initCanvas(): void {
    if (!this.canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const width = this.canvas.clientWidth || 400;
    const height = this.canvas.clientHeight || 160;
    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);
    this.ctx = this.canvas.getContext('2d');
    if (this.ctx) {
      this.ctx.scale(dpr, dpr);
      this.ctx.strokeStyle = '#1f2937';
      this.ctx.lineWidth = 2.5;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
    }
    this.hasDrawing = false;
  }

  private pointerPos(e: PointerEvent): { x: number; y: number } {
    const rect = this.canvas!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  onPointerDown(e: PointerEvent): void {
    if (!this.canvas || !this.ctx) return;
    e.preventDefault();
    this.canvas.setPointerCapture(e.pointerId);
    this.drawing = true;
    const { x, y } = this.pointerPos(e);
    this.lastX = x;
    this.lastY = y;
    this.ctx.beginPath();
    this.ctx.moveTo(x, y);
    this.ctx.lineTo(x + 0.1, y + 0.1);
    this.ctx.stroke();
    this.hasDrawing = true;
  }

  onPointerMove(e: PointerEvent): void {
    if (!this.drawing || !this.ctx) return;
    e.preventDefault();
    const { x, y } = this.pointerPos(e);
    const midX = (this.lastX + x) / 2;
    const midY = (this.lastY + y) / 2;
    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.quadraticCurveTo(this.lastX, this.lastY, midX, midY);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();
    this.lastX = x;
    this.lastY = y;
  }

  onPointerUp(e: PointerEvent): void {
    if (!this.drawing) return;
    this.drawing = false;
    if (this.canvas?.hasPointerCapture(e.pointerId)) {
      this.canvas.releasePointerCapture(e.pointerId);
    }
  }

  limpiarFirma(): void {
    if (!this.canvas || !this.ctx) return;
    const dpr = window.devicePixelRatio || 1;
    this.ctx.clearRect(0, 0, this.canvas.width / dpr, this.canvas.height / dpr);
    this.hasDrawing = false;
  }

  // ---- Subir imagen de firma ----

  onSignatureFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    this.uploadError = '';
    if (!file) return;
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      this.uploadError = 'Solo se permiten imágenes PNG o JPG.';
      input.value = '';
      return;
    }
    if (file.size > 1024 * 1024) {
      this.uploadError = 'La imagen no debe superar 1 MB.';
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.uploadedSignature = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  quitarImagenFirma(): void {
    this.uploadedSignature = null;
    this.uploadError = '';
  }
}
